import dayjs from "dayjs";
import { useState, useEffect } from "react";

// exclude these from the UI so users get the satisfaction of hitting 100% green in the donut chart
import API_Calls_IEB from "@/api/calls/API_Calls_IEB";
import { excludedLocals } from "@/components/IEBPage/excludedLocals";

export function useIEBPage() {
  const [IEBData, setIEBData] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [currentIEBDate, setCurrentIEBDate] = useState(null);
  const [notification, setNotification] = useState({});
  const [allRows, setAllRows] = useState([]);

  const { getIEBData, setFileAsIEB, changeIEBDate } = API_Calls_IEB();

  useEffect(() => {
    fetchIEBData();
    // fetchIEBData is an inline function recreated every render;
    // intentionally fire only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (IEBData) {
      createRows();
    }
    // createRows is an inline function recreated every render;
    // intentionally react only to IEBData changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IEBData]);

  const createRows = () => {
    const rows = [];
    if (IEBData) {
      Object.entries(IEBData).forEach(([local, data]) => {
        const localData = {
          name: local,
          IEBTimestamp: data.selected,
        };
        rows.push(localData);
      });
      setAllRows(rows);
    }
  };

  const fetchIEBData = async () => {
    const res = await getIEBData();
    if (!res.error && res.data) {
      const affiliates = res.data.affiliates;
      // filter excludedLocals from preferences so users will only see relevant locals in the UI
      const affiliateIEBData = Object.keys(affiliates)
        .filter((key) => !excludedLocals.includes(key))
        .reduce((obj, key) => {
          obj[key] = affiliates[key];
          return obj;
        }, {});
      setIEBData(affiliateIEBData);
      setDropdownOptions(Object.keys(affiliateIEBData));
      // convert date string from preferences.yaml to Day.js object
      setCurrentIEBDate(res.data.ieb_date ? dayjs(res.data.ieb_date) : null);
    } else {
      setNotification({
        message: "Unable to fetch IEB data at this time",
        severity: "error",
        duration: 7000,
      });
      console.error("Unable to fetch IEB data: ", res);
    }
  };

  const setFile = async (affiliate, processedDate) => {
    const response = await setFileAsIEB(affiliate, processedDate);
    if (!response.error) {
      setNotification({
        message: `IEB file has been set for ${affiliate}`,
        severity: "success",
        duration: 7000,
      });
      setIEBData((prevState) => ({
        ...prevState,
        [affiliate]: {
          ...prevState[affiliate],
          selected: processedDate,
        },
      }));
    } else {
      setNotification({
        message: `Unable to set IEB file for ${affiliate}`,
        severity: "error",
        duration: 7000,
      });
    }
  };

  const handleExport = async () => {
    const res = await getIEBData();
    if (!res.error && res.data) {
      const csvString = convertIEBToCSV(res.data);

      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "ieb_selection_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      setNotification({
        message: "Unable to export IEB data at this time",
        severity: "error",
        duration: 7000,
      });
      console.error("Unable to fetch IEB data: ", res);
    }
  };

  const convertIEBToCSV = (dataObject) => {
    const rows = Object.entries(dataObject).map(([local, details]) => {
      const cleanSelected = details.selected
        ? `"${String(details.selected)}"`
        : '""';
      return `${local},${cleanSelected}`;
    });
    const header = '"Local","IEB File"';
    return [header, ...rows].join("\n");
  };

  const updateIEBDate = async (newDate) => {
    const res = await changeIEBDate(newDate.format("YYYY-MM-DD"));

    if (!res.error) {
      setNotification({
        message: "New IEB date set and all IEB tags cleared",
        severity: "success",
        duration: 7000,
      });
      await fetchIEBData();
      return { success: true };
    } else {
      setNotification({
        message: "Unable to set new IEB date at this time",
        severity: "error",
        duration: 7000,
      });
      console.error("Unable to clear IEB data: ", res);
      return { success: false, error: res };
    }
  };

  return {
    IEBData,
    dropdownOptions,
    currentIEBDate,
    notification,
    setNotification,
    allRows,
    setFile,
    handleExport,
    updateIEBDate,
  };
}
